import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { ConversationStore } from './app-state/conversations.store';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterComponent } from './components/footer/footer.component';
import { InactivityService } from './services/inactivity.service';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    NavbarComponent,
    FooterComponent,
    MaintenanceComponent,
  ],
  templateUrl: './app.component.html',
  standalone: true,
  styleUrls: ['./app.component.scss'], // Fixed typo: changed "styleUrl" to "styleUrls"
})
export class AppComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  conversationStore = inject(ConversationStore);
  chats = this.conversationStore.conversations;
  isMaintenanceMode = false;
  private readonly router = inject(Router);

  get isAuthPage(): boolean {
    return this.router.url.startsWith('/auth');
  }

  private observer: MutationObserver | null = null; // Store observer reference
  private readonly inactivityService = inject(InactivityService);

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {
    // Check maintenance mode from localStorage (set by AppInitService)
    if (isPlatformBrowser(this.platformId)) {
      const maintenanceMode = localStorage.getItem('maintenanceMode');
      this.isMaintenanceMode = maintenanceMode === 'true';
    }

    if (!this.isMaintenanceMode) {
      this.conversationStore.getConversations();
    }
  }

  ngOnInit(): void {
    // Only run in browser environment
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      // This is a workaround to hide the hidden canvas elements created by the PDF viewer
      // Set up MutationObserver to monitor for dynamically created canvas elements
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes) {
            mutation.addedNodes.forEach((node) => {
              if (
                node.nodeType === Node.ELEMENT_NODE &&
                (node as Element).tagName === 'CANVAS' &&
                (node as HTMLElement).classList.contains('hiddenCanvasElement')
              ) {
                const canvasElement = node as HTMLElement;
                canvasElement.style.display = 'none';
                canvasElement.style.position = 'absolute';
                canvasElement.style.top = '-9999px';
                canvasElement.style.left = '-9999px';
              }
            });
          }
        });
      });

      // Start observing changes in the DOM
      this.observer.observe(document.body, { childList: true, subtree: true });

      // Note: Inactivity monitoring is now started after successful login
      // in two-factor-auth-form.component.ts and app-init.service.ts
    } catch (e) {
      console.error('Failed to start AppComponent services', e);
    }
  }

  ngOnDestroy(): void {
    // Disconnect the observer when the component is destroyed to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.inactivityService.stop();
  }
}
